import { revalidateLogic, useStore } from '@tanstack/react-form';
import { invalidateSignageQueries, noddiAsync, type SignageDeviceRecord, URLKeys } from 'noddi-async';

import { ApiErrorMessageInternal, NoddiCircularLoader, useAppForm } from 'noddi-ui-web';
import { signageAppLabel } from './signageCommandLog';
import {
  contentValuesFromDevice,
  SIGNAGE_REFRESH_INTERVAL_TOOLTIP,
  signageContentAssignSchema,
  signageContentAssignToBody
} from './signageContentAssign';
import { SignageContentAssignFields, type SignageContentTooltips } from './SignageContentAssignFields';
import { signageContentLabel } from './signageDeviceDisplay';
import { signageDisplayContentMode } from './signageDisplayContentMode';
import { SignageGrafanaKioskSettings } from './SignageGrafanaKioskSettings';
import { useSignageContentOptions } from './useSignageContentOptions';

type ContentSettingsProps = {
  device: SignageDeviceRecord;
  /**
   * Department scope for dashboard/playlist options.
   * Defaults to the device’s service department when set.
   */
  serviceDepartmentIds?: number[];
  tooltips?: SignageContentTooltips;
  /**
   * Display app id to bind content for — the parent's switcher selection, so
   * Grafana vs signage forms stay in sync with the exclusive screen owner.
   */
  displayApp: string;
};

type PlayerContentProps = {
  device: SignageDeviceRecord;
  serviceDepartmentIds?: number[];
  tooltips?: SignageContentTooltips;
};

const DEFAULT_TOOLTIPS: SignageContentTooltips = {
  content:
    'What the screen should show: a named dashboard, rotating playlist, free-form URL, or nothing until you assign content later.',
  dashboard: 'Named widget dashboard rendered fullscreen on the device.',
  playlist: 'Rotating playlist of media / dashboards / URLs.',
  assignedUrl:
    'Full URL the browser player loads fullscreen (https://…). Use for external pages when you are not using a dashboard or playlist.',
  refreshIntervalSeconds: `${SIGNAGE_REFRESH_INTERVAL_TOOLTIP} Default 60.`
};

/**
 * Content for a display app — adapts to which exclusive screen owner is targeted:
 * - grafana-kiosk → public dashboard pin ({@link SignageGrafanaKioskSettings})
 * - signage (and other browser players) → dashboard / playlist / URL assign
 * - unsupported apps → empty state (no staff content binding)
 *
 * Intended for embedding in {@link SignageDeviceContentConfigCard} (no outer card).
 */
export const SignageDeviceContentSettings = ({
  device,
  serviceDepartmentIds,
  tooltips,
  displayApp
}: ContentSettingsProps) => {
  const appId = displayApp.trim();
  const mode = signageDisplayContentMode(appId);

  if (mode === 'grafana-kiosk') {
    return <SignageGrafanaKioskSettings device={device} />;
  }

  if (mode === 'unsupported') {
    const label = signageAppLabel(appId) ?? appId;
    return (
      <div className='flex flex-col gap-2'>
        <h3 className='text-sm font-medium'>Content</h3>
        <p className='text-xs text-muted-foreground'>
          {label} does not use content assignment in this screen. Switch to Signage or Grafana kiosk to configure what
          the display shows.
        </p>
      </div>
    );
  }

  return (
    <SignagePlayerContentSettings device={device} serviceDepartmentIds={serviceDepartmentIds} tooltips={tooltips} />
  );
};

const SignagePlayerContentSettings = ({ device, serviceDepartmentIds, tooltips }: PlayerContentProps) => {
  const contentDefaults = contentValuesFromDevice(device);

  const departmentIds =
    serviceDepartmentIds ??
    (device.serviceDepartmentId != null && device.serviceDepartmentId > 0 ? [device.serviceDepartmentId] : undefined);

  const options = useSignageContentOptions({ serviceDepartmentIds: departmentIds });

  const { mutateAsync: patchDevice } = noddiAsync.usePatch({
    type: URLKeys.patchSignageDevice,
    queryConfig: {
      onSuccess: () => {
        void invalidateSignageQueries();
      }
    }
  });

  const form = useAppForm({
    defaultValues: contentDefaults,
    validationLogic: revalidateLogic(),
    validators: { onDynamic: signageContentAssignSchema },
    onSubmit: async ({ value }) => {
      const valid = signageContentAssignSchema.parse(value);
      await patchDevice({
        pk: device.id,
        ...signageContentAssignToBody(valid)
      });
    }
  });

  const contentType = useStore(form.store, (state) => state.values.contentType);
  const currentLabel = signageContentLabel(device);
  const fieldTooltips = { ...DEFAULT_TOOLTIPS, ...tooltips };

  return (
    <div className='flex flex-col gap-4'>
      <div>
        <h3 className='text-sm font-medium'>Assigned content</h3>
        <p className='mt-1 text-xs text-muted-foreground'>
          Choose what this screen shows: a named dashboard, a rotating playlist, a free-form URL, or nothing. Current:{' '}
          <span className='font-medium text-foreground'>{currentLabel}</span>. Changes apply on the next content poll.
        </p>
      </div>

      {options.loadError && <ApiErrorMessageInternal error={options.loadError} />}
      {options.isLoadingOptions && (
        <div className='flex justify-center py-2'>
          <NoddiCircularLoader />
        </div>
      )}

      <form.AppForm>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            e.stopPropagation();
            form.handleSubmit();
          }}
          className='flex flex-col gap-4'
        >
          <SignageContentAssignFields
            AppField={form.AppField}
            contentType={contentType}
            dashboardOptions={options.dashboardOptions}
            playlistOptions={options.playlistOptions}
            layout='grid'
            tooltips={fieldTooltips}
          />

          <div className='flex justify-end'>
            <form.SubscribeButton label='Save content' />
          </div>
        </form>
      </form.AppForm>
    </div>
  );
};
